import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Loader2, IndianRupee, Send, Copy, ExternalLink, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { shareViaWhatsApp, WhatsAppIcon } from '../../lib/whatsapp';

const fmtINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Mirror of backend ADDON_TYPES (see _vo_common.py). Keep in sync.
const ADDON_CATALOG = {
  bulk_day_pass: {
    label: 'Bulk Day Pass',
    unit_label: 'passes',
    rate_fixed: null,
    needs_validity: true,
    hint: 'Sales enters cost per pass. Each pass converts to 5 credits.',
  },
  meeting_credits: {
    label: 'Meeting Room Credits',
    unit_label: 'credits',
    rate_fixed: 100,
    needs_validity: true,
    hint: 'Fixed ₹100/credit. Sales chooses quantity & validity.',
  },
  lobby_listing: {
    label: 'Lobby Listing',
    unit_label: 'months',
    rate_fixed: null,
    needs_validity: false,
    hint: 'Sales enters cost per month and duration.',
  },
  stamp_paper: {
    label: 'Stamp Paper',
    unit_label: 'stamp papers',
    rate_fixed: null,
    needs_validity: false,
    hint: 'Enter number of stamp papers and the cost per stamp paper.',
  },
};

const newBlankAddon = (type) => ({
  type,
  quantity: type === 'bulk_day_pass' ? 10 : '',
  unit_price: ADDON_CATALOG[type].rate_fixed ?? '',
  validity_days: '',
});

const SellVirtualOfficeModal = ({ open, onClose, api, defaultVenueId = null }) => {
  const [step, setStep] = useState('form');     // form | success
  const [venues, setVenues] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    venue_id: defaultVenueId || '',
    plan_id: '',
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    gstin: '',
    company_address: '',
    discount_percentage: 0,
    discount_reason: '',
    addons: [],
  });

  useEffect(() => {
    if (!open) return;
    setStep('form');
    setResult(null);
    setForm({
      venue_id: defaultVenueId || '',
      plan_id: '', company_name: '', contact_name: '', email: '', phone: '', gstin: '', company_address: '',
      discount_percentage: 0, discount_reason: '', addons: [],
    });
    (async () => {
      setLoading(true);
      try {
        const [v, p] = await Promise.all([
          api.get('/virtual-office/managed-venues'),
          api.get('/virtual-office/plans'),
        ]);
        setVenues(v.data || []);
        setPlans((p.data || []).filter((x) => x.is_active));
      } catch (e) {
        toast.error('Failed to load venues/plans');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, api, defaultVenueId]);

  const venuePlans = plans.filter((p) => p.venue_id === form.venue_id);
  const selectedPlan = plans.find((p) => p.id === form.plan_id);
  const voBase = selectedPlan ? selectedPlan.monthly_price * 12 : 0;

  const addonLineTotal = (a) => {
    const qty = Number(a.quantity) || 0;
    const price = Number(a.unit_price) || 0;
    return +(qty * price).toFixed(2);
  };
  const addonsTotal = form.addons.reduce((s, a) => s + addonLineTotal(a), 0);
  const listBase = +(voBase + addonsTotal).toFixed(2);
  const discountPct = Math.max(0, Math.min(50, Number(form.discount_percentage) || 0));
  const discountAmount = +(listBase * discountPct / 100).toFixed(2);
  const annualBase = +(listBase - discountAmount).toFixed(2);
  const gst = +(annualBase * 0.18).toFixed(2);
  const total = +(annualBase + gst).toFixed(2);

  const addAddon = (type) => {
    if (form.addons.some((a) => a.type === type)) {
      toast.error(`${ADDON_CATALOG[type].label} already added`);
      return;
    }
    setForm({ ...form, addons: [...form.addons, newBlankAddon(type)] });
  };
  const updateAddon = (idx, patch) => {
    const next = form.addons.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    setForm({ ...form, addons: next });
  };
  const removeAddon = (idx) => {
    setForm({ ...form, addons: form.addons.filter((_, i) => i !== idx) });
  };

  const submit = async () => {
    if (!form.plan_id || !form.company_name || !form.contact_name || !form.email || !form.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    if (discountPct > 0 && !form.discount_reason.trim()) {
      toast.error('Please add a reason when applying a discount');
      return;
    }
    // Validate each addon
    for (const a of form.addons) {
      const meta = ADDON_CATALOG[a.type];
      if (!a.quantity || Number(a.quantity) < 1) {
        toast.error(`${meta.label}: quantity required`);
        return;
      }
      if (meta.rate_fixed === null && (!a.unit_price || Number(a.unit_price) <= 0)) {
        toast.error(`${meta.label}: unit price required`);
        return;
      }
      if (meta.needs_validity && (!a.validity_days || Number(a.validity_days) < 1)) {
        toast.error(`${meta.label}: validity (days) required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payloadAddons = form.addons.map((a) => {
        const meta = ADDON_CATALOG[a.type];
        return {
          type: a.type,
          quantity: Number(a.quantity),
          unit_price: meta.rate_fixed ?? Number(a.unit_price),
          validity_days: meta.needs_validity ? Number(a.validity_days) : null,
        };
      });
      const res = await api.post('/virtual-office/checkout/sales-link', {
        plan_id: form.plan_id,
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        gstin: form.gstin || null,
        company_address: form.company_address || null,
        discount_percentage: discountPct,
        discount_reason: form.discount_reason || null,
        addons: payloadAddons,
      });
      setResult(res.data);
      setStep('success');
      toast.success('Payment link generated and emailed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate payment link');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.payment_link_url);
    toast.success('Link copied');
  };

  const shareWA = () => {
    const venue = venues.find((v) => v.id === form.venue_id);
    const discLine = (result.discount_percentage || 0) > 0
      ? `\n🎁 Special discount: ${result.discount_percentage}% off`
      : '';
    const msg = `Hi ${form.contact_name},\n\nHere's the payment link for your *Virtual Office* subscription:\n\n🏢 Plan: ${result.tier_name}\n📍 Centre: ${venue?.name}${discLine}\n💰 Amount: ${fmtINR(result.total_amount)} (incl. GST · 12-month term)\n\n${result.payment_link_url}\n\nOnce paid, your subscription activates immediately. — Clayworks Team`;
    shareViaWhatsApp(msg, form.phone);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vo-sell-modal">
        <DialogHeader>
          <DialogTitle>{step === 'form' ? 'Sell Virtual Office Plan' : 'Payment Link Sent ✓'}</DialogTitle>
        </DialogHeader>

        {loading && <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

        {!loading && step === 'form' && (
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Centre *</Label>
                <Select value={form.venue_id} onValueChange={(v) => setForm({ ...form, venue_id: v, plan_id: '' })}>
                  <SelectTrigger data-testid="vo-sell-venue"><SelectValue placeholder="Select centre" /></SelectTrigger>
                  <SelectContent>
                    {venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Plan *</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })} disabled={!form.venue_id}>
                  <SelectTrigger data-testid="vo-sell-plan"><SelectValue placeholder={form.venue_id ? 'Select plan' : 'Pick a centre first'} /></SelectTrigger>
                  <SelectContent>
                    {venuePlans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.tier_name} · {fmtINR(p.monthly_price)}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Company Name *</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="vo-sell-company" />
              </div>
              <div>
                <Label>Contact Name *</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="vo-sell-contact" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vo-sell-email" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vo-sell-phone" />
              </div>
            </div>

            <div>
              <Label>GSTIN (optional)</Label>
              <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="22ABCDE1234F1Z5" data-testid="vo-sell-gstin" maxLength={15} />
            </div>
            {form.gstin && (
              <div>
                <Label>Company Address (for invoice)</Label>
                <Input value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} data-testid="vo-sell-address" />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount % (optional, max 50)</Label>
                <Input
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                  value={form.discount_percentage}
                  onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })}
                  placeholder="0"
                  data-testid="vo-sell-discount-pct"
                />
              </div>
              <div>
                <Label>Discount Reason {discountPct > 0 ? '*' : ''}</Label>
                <Input
                  value={form.discount_reason}
                  onChange={(e) => setForm({ ...form, discount_reason: e.target.value })}
                  placeholder={discountPct > 0 ? 'e.g. Loyalty / Bulk / Campaign' : '—'}
                  disabled={discountPct === 0}
                  data-testid="vo-sell-discount-reason"
                />
              </div>
            </div>

            {/* ───── Add-ons ───── */}
            <div className="border-t border-border pt-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Add-ons (optional)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(ADDON_CATALOG).map(([type, meta]) => {
                    const exists = form.addons.some((a) => a.type === type);
                    return (
                      <Button
                        key={type}
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        disabled={exists}
                        onClick={() => addAddon(type)}
                        data-testid={`vo-addon-add-${type}`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> {meta.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
              {form.addons.length === 0 && (
                <p className="text-xs text-muted-foreground italic">No add-ons. Click a button above to bundle one with the subscription.</p>
              )}
              {form.addons.map((a, idx) => {
                const meta = ADDON_CATALOG[a.type];
                return (
                  <div key={a.type} className="p-2.5 rounded-lg bg-muted/30 border border-border space-y-2" data-testid={`vo-addon-row-${a.type}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">{meta.label}</Badge>
                        <span className="text-xs text-muted-foreground">{meta.hint}</span>
                      </div>
                      <Button type="button" size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeAddon(idx)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Quantity ({meta.unit_label}) *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={a.quantity}
                          onChange={(e) => updateAddon(idx, { quantity: e.target.value })}
                          data-testid={`vo-addon-qty-${a.type}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">
                          Unit Price (₹) {meta.rate_fixed != null ? '(fixed)' : '*'}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={meta.rate_fixed != null ? meta.rate_fixed : a.unit_price}
                          onChange={(e) => updateAddon(idx, { unit_price: e.target.value })}
                          disabled={meta.rate_fixed != null}
                          data-testid={`vo-addon-price-${a.type}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{meta.needs_validity ? 'Validity (days) *' : 'Line Total'}</Label>
                        {meta.needs_validity ? (
                          <Input
                            type="number"
                            min="1"
                            value={a.validity_days}
                            onChange={(e) => updateAddon(idx, { validity_days: e.target.value })}
                            placeholder="e.g. 90"
                            data-testid={`vo-addon-validity-${a.type}`}
                          />
                        ) : (
                          <div className="px-2 py-2 text-sm font-semibold">{fmtINR(addonLineTotal(a))}</div>
                        )}
                      </div>
                    </div>
                    {meta.needs_validity && (
                      <div className="text-xs text-right text-muted-foreground">
                        Line total: <strong className="text-foreground">{fmtINR(addonLineTotal(a))}</strong>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {selectedPlan && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-1">
                <div className="flex justify-between text-sm"><span>VO Plan — Annual ({selectedPlan.tier_name})</span><span>{fmtINR(voBase)}</span></div>
                {form.addons.map((a) => (
                  <div key={a.type} className="flex justify-between text-sm text-muted-foreground">
                    <span>+ {ADDON_CATALOG[a.type].label} × {a.quantity || 0} {ADDON_CATALOG[a.type].unit_label}</span>
                    <span>{fmtINR(addonLineTotal(a))}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-medium pt-1 border-t border-primary/10">
                  <span>List Subtotal</span><span>{fmtINR(listBase)}</span>
                </div>
                {discountPct > 0 && (
                  <div className="flex justify-between text-sm text-emerald-700 dark:text-emerald-400">
                    <span>Sales Discount ({discountPct}%)</span>
                    <span>- {fmtINR(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm"><span>Discounted Base</span><span>{fmtINR(annualBase)}</span></div>
                <div className="flex justify-between text-sm"><span>GST (18%)</span><span>{fmtINR(gst)}</span></div>
                <div className="flex justify-between font-semibold text-base pt-1 border-t border-primary/20"><span>Total Payable</span><span className="text-primary">{fmtINR(total)}</span></div>
                <p className="text-xs text-muted-foreground italic">12-month commitment · paid annually · non-refundable except by admin discretion</p>
              </div>
            )}
          </div>
        )}

        {!loading && step === 'success' && result && (
          <div className="space-y-3 py-2">
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30">
              <p className="text-sm font-semibold text-green-700 dark:text-green-400">Payment link emailed to {form.email}</p>
              <p className="text-xs text-muted-foreground mt-1">Subscription is pending payment. Once paid, it activates immediately and a Zoho invoice is auto-generated.</p>
            </div>
            <div className="space-y-2">
              <Label>Payment Link</Label>
              <div className="flex gap-2">
                <Input value={result.payment_link_url} readOnly className="font-mono text-xs" />
                <Button size="sm" variant="outline" onClick={copyLink} data-testid="vo-sell-copy">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(result.payment_link_url, '_blank')}>
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <Button onClick={shareWA} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="vo-sell-whatsapp">
              <WhatsAppIcon className="w-4 h-4 mr-2" /> Share via WhatsApp
            </Button>
            <div className="p-2.5 rounded bg-muted/30 text-sm space-y-0.5">
              <div className="flex justify-between"><span>Plan</span><strong>{result.tier_name}</strong></div>
              <div className="flex justify-between"><span>Total</span><strong>{fmtINR(result.total_amount)}</strong></div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'form' ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={submitting || !selectedPlan} data-testid="vo-sell-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Generate Payment Link
              </Button>
            </>
          ) : (
            <Button onClick={onClose} data-testid="vo-sell-done">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SellVirtualOfficeModal;
